import React, { useEffect, useContext, useState } from "react"
import {
  GET_STAFF_NAMES,
  GET_ORGANIZATION,
  findName,
  getTimeAgo,
  staffNameInitials,
} from "../Utils"
import { UserContext } from "../../../context/userContext"
import { useQuery, useLazyQuery } from "@apollo/client"
import trcss from "../../../styles/Telerecovery.module.css"
import ChatComponent from "./ChatComponent"
import {
  trApiGetChatConversationSid,
  trApiChatConvSetRead,
} from "../telerecoveryApi"
import { sortListByDate } from "../Utils"
import { RLinkLoader } from "../../../components/Loaders"
import { LogError } from "../../../helpers/logger"

const TRChatMessagesIndex = ({
  staffId,
  staffName,
  selectedConvStaffId,
  changeSelectedConv,
  activePanel,
  setActivePanel, // Allow updating panel state
}) => {
  const userCtx = useContext(UserContext)
  const [chatConvs, setChatConvs] = useState(null)
  const [selChatConvSid, setSelChatConvSid] = useState(null)
  const [loading, setLoading] = useState(false)
  const noAssignedStaff = staffId ? false : true

  const { refetch: refetchStaffNames } = useQuery(GET_STAFF_NAMES, {
    variables: {
      staff_id: "",
      organization_id: "",
      staff_ids: [],
    },
    skip: true,
  })

  const [fetchOrganization] = useLazyQuery(GET_ORGANIZATION)

  const getStaffNames = async (convList) => {
    const validStaffList = convList.filter(
      (item) => item.organizationId !== null,
    )

    const staffByOrg = validStaffList.reduce((acc, item) => {
      if (!acc[item.organizationId]) acc[item.organizationId] = new Set()
      acc[item.organizationId].add(item.staffId)
      return acc
    }, {})

    const unknownStaff = {
      staff_id: null,
      staff_name_first: "Removed",
      staff_name_last: "User",
      staff_full_name: "Removed User",
    }

    let staffNameMap = {}
    let orgNameMap = {} // Store organization names

    try {
      // Fetch staff names
      for (const [organizationId, staffIdsSet] of Object.entries(staffByOrg)) {
        const staffIds = [...staffIdsSet]

        if (staffIds.length > 0) {
          const res = await refetchStaffNames({
            staff_id: staffIds[0],
            organization_id: organizationId,
            staff_ids: staffIds,
          })

          if (res.data?.getStaffNamesByIds?.data) {
            const rawData = JSON.parse(res.data.getStaffNamesByIds.data)
            Object.values(rawData).forEach((entry) => {
              staffNameMap[entry.staff_id] = entry
            })
          }
        }

        // Fetch organization name correctly
        const { data: orgData } = await fetchOrganization({
          variables: { staff_id: staffIds[0], organization_id: organizationId },
        })

        if (orgData?.organization?.organization_name) {
          orgNameMap[organizationId] = orgData.organization.organization_name
        }
      }

      // Return updated conversation list
      return validStaffList.map((item) => ({
        ...item,
        staffName: staffNameMap[item.staffId] || {
          ...unknownStaff,
          staff_id: item.staffId,
        },
        organizationName:
          orgNameMap[item.organizationId] || "Unknown Organization",
      }))
    } catch (error) {
      LogError("Error fetching staff names and organization names:", error)
      return validStaffList.map((item) => ({
        ...item,
        staffName: { ...unknownStaff, staff_id: item.staffId },
        organizationName: "Unknown Organization",
      }))
    }
  }

  const markConversationRead = (convSid) => {
    if (!chatConvs || chatConvs.length === 0) return // Prevents calling map on null

    trApiChatConvSetRead(userCtx.tokens.getTelerecoveryToken(), {
      convSid,
      fromPeer: false,
    }).then((resp) => {
      const updatedList = chatConvs.map((conv) => {
        if (conv.sid === convSid) {
          return { ...conv, participantContainsUnread: false }
        }
        return conv
      })
      setChatConvs(updatedList)
    })
  }

  useEffect(() => {
    if (chatConvs && chatConvs.length > 1 && selectedConvStaffId) {
      const index = chatConvs.findIndex(
        (conv) => conv.staffId === selectedConvStaffId,
      )

      if (index !== -1) {
        setSelChatConvSid({
          ...chatConvs[index],
          index,
          participantContainsUnread: false, // Setting it to false
        })
      }
    }
  }, [selectedConvStaffId, chatConvs])

  const selectConversation = (conv, index) => {
    setSelChatConvSid({ ...conv, index })
    markConversationRead(conv.sid)
    changeSelectedConv(conv.staffName.full_name)
    setActivePanel("chat")
  }

  useEffect(() => {
    if (!noAssignedStaff) {
      setLoading(true)

      // Get the organization ID for the current staff
      const currentPtpOrgId =
        userCtx.participant_organizations.find((org) =>
          org.staff_assignment_list.some(
            (staffList) => staffList.staff_id === staffId,
          ),
        )?.organization_id ||
        userCtx.participant_organizations[0]?.organization_id // Fallback to first org if not found

      const fetchConversations = async () => {
        try {
          const response = await trApiGetChatConversationSid(
            userCtx.tokens.getTelerecoveryToken(),
            staffId,
            userCtx.participant_id,
            currentPtpOrgId,
          )

          let _chatConvs = response

          // Fetch staff names along with organization names
          const updatedConversations = await getStaffNames(_chatConvs)

          _chatConvs = _chatConvs.map((conv) => {
            // Find the corresponding conversation data with organization name
            const matchingConv = updatedConversations.find(
              (c) =>
                c.staffId === conv.staffId &&
                c.organizationId === conv.organizationId,
            )

            return {
              ...conv,
              staffName: findName(conv.staffId, updatedConversations),
              organizationName:
                matchingConv?.organizationName || "Unknown Organization", // Set organization name
              lastMessageAt: conv.lastMessageAt
                ? conv.lastMessageAt + "Z"
                : conv.lastMessageAt,
            }
          })

          // Remove conversations with "Removed" staff
          _chatConvs = _chatConvs.filter(
            (conv) => conv.staffName.first !== "Removed",
          )

          // Sort by date and update state
          sortListByDate(_chatConvs, "lastMessageAt")
          setChatConvs(_chatConvs)

          if (_chatConvs.length > 0 && window.innerWidth >= 767) {
            selectConversation(_chatConvs[0], 0)
          }
        } catch (error) {
          LogError("Error fetching conversations:", error)
        } finally {
          setLoading(false)
        }
      }

      fetchConversations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.participant_id])

  if (loading) {
    return <RLinkLoader></RLinkLoader>
  }

  const updateConvList = (newMsg) => {
    const updatedChatConvs = chatConvs.map((conv, index) => {
      if (conv.staffId === newMsg.fromId && conv.sid === newMsg.convSid) {
        return {
          ...conv,
          lastMessageAt: newMsg.dateUpdated,
          lastMessage: newMsg.body,
          participantContainsUnread:
            selChatConvSid.staffId !== newMsg.fromId
              ? true
              : conv.participantContainsUnread,
        }
      } else if (
        selChatConvSid.sid === conv.sid &&
        newMsg.fromId === userCtx.participant_id
      ) {
        return {
          ...conv,
          lastMessageAt: newMsg.dateUpdated,
          lastMessage: newMsg.body,
        }
      }
      return conv
    })

    sortListByDate(updatedChatConvs, "lastMessageAt")
    setChatConvs(updatedChatConvs)
  }

  const truncateText = (text, maxLength = 25) => {
    if (!text || typeof text !== "string") return "" // Handle null, undefined, or non-string values

    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text
  }

  return (
    <div className="grid">
      <div
        className={`col-12 md:col-3 convListContainer ${
          activePanel === "chat" ? "hidden-on-mobile" : ""
        }`}
      >
        <div className={trcss.chatConvMenu + " rlink-card"}>
          <h4
            style={{
              fontWeight: "700",
              marginBottom: "15px",
              marginLeft: "15px",
              marginTop: "20px",
            }}
          >
            Messages
          </h4>
          {chatConvs &&
            chatConvs.map((conv, index) => {
              return (
                <button
                  style={{
                    width: "100%",
                    position: "relative",
                    borderRight:
                      conv.sid === selChatConvSid.sid ? "2px solid purple" : "",
                  }}
                  key={`${conv.sid}_${index}`}
                  onClick={() => selectConversation(conv, index)}
                >
                  <div style={{ paddingLeft: "15px", paddingRight: "15px" }}>
                    <hr
                      style={{
                        border: "1px solid rgb(231, 224, 241)",
                        margin: "auto",
                      }}
                    />
                  </div>

                  <div
                    className={`${trcss.chatConvElement} ${
                      selChatConvSid &&
                      selChatConvSid.index === index &&
                      trcss.chatConvElementSelected
                    }`}
                  >
                    {/* User image - circle with initials */}
                    <span
                      className={trcss.userInitials}
                      style={{
                        marginTop: "-5px",
                        width: "36px",
                        minWidth: "36px",
                      }}
                    >
                      {staffNameInitials(conv.staffName)}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        paddingTop: "20px",
                        paddingBottom: "20px",
                        paddingLeft: "7px",
                      }}
                    >
                      {/* First row: Left text and Right text aligned horizontally */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <h6 style={{ margin: 0, textAlign: "left" }}>
                          {conv.staffName.full_name}
                        </h6>
                        <p
                          style={{
                            margin: 0,
                            marginTop: "3px",
                            color: "#999999",
                          }}
                        >
                          {getTimeAgo(conv.lastMessageAt)}
                        </p>
                      </div>

                      {/* Second row: Organization Name (Ensuring it stays below) */}
                      <p
                        style={{
                          fontSize: "11px",
                          marginTop: "0px",
                          marginBottom: "5px",
                        }}
                      >
                        {conv.organizationName}
                      </p>
                      {/* Third row: Last message preview & unread count */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <p style={{ margin: 0, color: "#999999" }}>
                          {conv.lastMessage && truncateText(conv.lastMessage)}
                          {(!conv.lastMessage || conv.lastMessage === "") &&
                            "This conversation is empty."}
                        </p>
                        {conv.participantContainsUnread &&
                          conv.participantContainsUnread === true &&
                          conv.staffId !== selectedConvStaffId && (
                            <p className={trcss.unreadMessagesCount} />
                          )}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          <div style={{ paddingLeft: "15px", paddingRight: "15px" }}>
            <hr
              style={{
                border: "1px solid rgb(231, 224, 241)",
                margin: "auto",
              }}
            />
          </div>
        </div>
      </div>

      {selChatConvSid && (
        <div
          className={`col-12 md:col-9 chatConvContainer ${
            activePanel === "list" ? "hidden-on-mobile" : ""
          }`}
        >
          <div className={trcss.chatComponent}>
            <ChatComponent
              chatConvSid={selChatConvSid.sid}
              staffId={selChatConvSid.staffId}
              staffName={selChatConvSid.staffName}
              organizationId={selChatConvSid.organizationId}
              onNewMessage={updateConvList}
            />
          </div>
        </div>
      )}
      {noAssignedStaff && (
        <p>
          Currently, there is no staff member assigned to you. Kindly reach out
          to an administrator for further assistance.
        </p>
      )}
    </div>
  )
}
export default TRChatMessagesIndex
